/**
 * Import notification utilities
 * 
 * Provides functions for creating various types of notifications
 * used throughout the import process.
 */

import { pluralize } from '../commons'
import { errorIcon } from '../../components/Notification'
import type { NotificationType } from '../../components/Notification'
import type { ImportStats } from './types'

/**
 * Builds a compact, user-friendly message summarizing import results
 *
 * Extracted from useImportMarkdown to be shared across all import operations.
 * Creates messages like "2 folders created, 3 files imported" or "1 folder reused, 2 files skipped"
 *
 * @param stats - Import statistics to summarize
 * @param attachmentFailures - Number of attachment upload failures (optional)
 * @returns Human-readable summary string
 */
export function buildCompactImportMessage(stats: ImportStats, attachmentFailures?: number): string {
  const parts: string[] = []

  // Folder messages (show created and reused separately)
  if (stats.foldersCreated > 0) {
    parts.push(`${String(stats.foldersCreated)} ${pluralize('folder', stats.foldersCreated)} created`)
  }

  if (stats.foldersReused > 0) {
    parts.push(`${String(stats.foldersReused)} ${pluralize('folder', stats.foldersReused)} reused`)
  }

  // File messages
  if (stats.created > 0) {
    parts.push(`${String(stats.created)} ${pluralize('file', stats.created)} imported`)
  }

  if (stats.overwritten > 0) {
    parts.push(`${String(stats.overwritten)} ${pluralize('file', stats.overwritten)} overwritten`)
  }

  if (stats.duplicated > 0) {
    parts.push(`${String(stats.duplicated)} ${pluralize('file', stats.duplicated)} duplicated`)
  }

  if (stats.skipped > 0) {
    parts.push(`${String(stats.skipped)} ${pluralize('file', stats.skipped)} skipped`)
  }

  if (stats.errors > 0) {
    parts.push(`${String(stats.errors)} ${pluralize('error', stats.errors)}`)
  }

  if (attachmentFailures && attachmentFailures > 0) {
    parts.push(`${String(attachmentFailures)} attachment ${pluralize('failure', attachmentFailures)}`)
  }

  return parts.length > 0 ? parts.join(', ') : 'No files processed'
}

/**
 * Creates a processing notification for import operations
 * 
 * @param itemCount - Number of items being processed
 * @param itemType - Type of items (e.g., "file", "folder")
 * @returns Notification object for showing processing status
 */
export function createProcessingNotification(itemCount: number, itemType: string): NotificationType {
  return {
    title: 'Importing…',
    message: `Processing ${String(itemCount)} ${pluralize(itemType, itemCount)}`,
    autoHide: false,
  }
}

/**
 * Creates a completion notification for import operations
 *
 * @param stats - Final import statistics
 * @param attachmentFailures - Number of attachment upload failures (optional)
 * @returns Notification object for showing completion status
 */
export function createCompletionNotification(stats: ImportStats, attachmentFailures?: number): NotificationType {
  const message = buildCompactImportMessage(stats, attachmentFailures)
  const hasErrors = stats.errors > 0
  const hasAttachmentFailures = attachmentFailures && attachmentFailures > 0
  const hasIssues = hasErrors || hasAttachmentFailures

  return {
    title: hasIssues ? 'Import completed with issues' : 'Import completed',
    message,
    icon: hasIssues ? errorIcon : undefined,
    autoHide: !hasIssues, // Keep error notifications visible
  }
}

/**
 * Creates an error notification for import operations
 * 
 * @param title - Error title
 * @param message - Error message
 * @returns Notification object for showing error
 */
export function createErrorNotification(title: string, message: string): NotificationType {
  return {
    title,
    message,
    icon: errorIcon,
    autoHide: false,
  }
}

/**
 * Creates a notification for when many files failed to import
 * 
 * @param errorCount - Number of errors
 * @param totalCount - Total number of files attempted
 * @returns Notification object if significant errors occurred, undefined otherwise
 */
export function createSignificantErrorNotification(
  errorCount: number, 
  totalCount: number
): NotificationType | undefined {
  if (errorCount > 0 && errorCount >= totalCount / 2) {
    return {
      title: 'Import Issues',
      message: 'Many files could not be imported. Check that they are valid text files.',
      icon: errorIcon,
      autoHide: false,
    }
  }
  return undefined
}

